import { useState } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, Search, AlertTriangle, Brain, Shield, Check } from 'lucide-react';

const ITEMS = [
  {
    Icon: Newspaper,
    color: '#60A5FA',
    text: 'Everything you see here is sourced from publicly available data. Every item links back to its original source — tap any trend to see where it came from.',
  },
  {
    Icon: Search,
    color: '#67E8F9',
    text: "We aggregate and surface trends — we don't alter, interpret, or editorialize the underlying data in any way.",
  },
  {
    Icon: AlertTriangle,
    color: '#FBBF24',
    text: 'WTFHappened is not a financial advisory service and is not regulated as one. Nothing here is financial advice.',
  },
  {
    Icon: Brain,
    color: '#F472B6',
    text: 'Always do your own research before making any financial or investment decision.',
  },
  {
    Icon: Shield,
    color: '#6EE7B7',
    text: 'We accept no liability for any losses arising from decisions made using this app.',
  },
];

export default function DisclaimerScreen({ onAccept }) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    try { await onAccept(); } finally { setSubmitting(false); }
  };

  return (
    <motion.div
      data-testid="disclaimer-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center px-6 overflow-y-auto"
      style={{ background: '#0d0d0d' }}
    >
      <div className="w-full max-w-md py-10">
        <div className="w-12 h-1 rounded-full mb-6" style={{ background: '#EF4444' }} />

        <h1 className="text-3xl font-black text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          Before You Dive In
        </h1>
        <p className="text-xs text-gray-500 mb-8 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
          You'll only see this once. After you accept, it stays in Settings → Advisory.
        </p>

        <div className="space-y-5 mb-8">
          {ITEMS.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + i * 0.1 }}
              className="flex gap-4 items-start"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
                style={{ background: `${item.color}15` }}>
                <item.Icon size={16} style={{ color: item.color }} />
              </div>
              <p className="text-[15px] text-gray-300 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>

        <p className="text-sm text-gray-500 italic mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Full terms available in our Legal section.
        </p>

        {/* Required consent tickbox — accept button disabled until ticked */}
        <motion.button
          data-testid="disclaimer-consent-checkbox"
          onClick={() => setAgreed(v => !v)}
          type="button"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75 }}
          aria-checked={agreed}
          role="checkbox"
          className="w-full flex items-start gap-3 mb-5 p-4 rounded-xl border text-left transition-all"
          style={{
            background: agreed ? 'rgba(239, 68, 68, 0.08)' : 'rgba(255,255,255,0.03)',
            borderColor: agreed ? 'rgba(239, 68, 68, 0.5)' : 'rgba(255,255,255,0.1)',
          }}
        >
          <div
            className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center mt-0.5 transition-all"
            style={{
              background: agreed ? '#EF4444' : 'transparent',
              border: agreed ? '1px solid #EF4444' : '1px solid rgba(255,255,255,0.3)',
            }}
          >
            {agreed && <Check size={14} className="text-white" strokeWidth={3} />}
          </div>
          <p className="text-[13px] text-gray-200 leading-snug" style={{ fontFamily: 'Manrope, sans-serif' }}>
            I have read and understood the above. I acknowledge that WTFHappened is not a
            financial advisory service, and I accept these terms.
          </p>
        </motion.button>

        <motion.button
          data-testid="disclaimer-accept"
          onClick={handleAccept}
          disabled={!agreed || submitting}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
          whileTap={agreed && !submitting ? { scale: 0.97 } : undefined}
          className="w-full py-4 rounded-2xl text-white text-lg font-bold transition-all"
          style={{
            background: agreed
              ? 'linear-gradient(to right, #EF4444, #EC4899)'
              : 'rgba(255,255,255,0.08)',
            boxShadow: agreed ? '0 6px 24px rgba(239, 68, 68, 0.3)' : 'none',
            opacity: agreed && !submitting ? 1 : 0.55,
            cursor: agreed && !submitting ? 'pointer' : 'not-allowed',
          }}
        >
          {submitting ? 'Recording…' : (agreed ? 'I Accept' : 'Tick the box to continue')}
        </motion.button>
      </div>
    </motion.div>
  );
}
